import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { motion } from "motion/react";
import { Image, YoutubeThumbnail } from "@components";
import { ANIMATION_CONFIG } from "../../constants";
import { easeOutQuad } from '@/core/easing';
import { replaceStringVar } from "@/core/utils";

// GSAP
import gsap from "gsap";
import Draggable from "gsap/Draggable";

// Styles
import styles from "../RHTV-Modal.module.scss";
import className from "classnames/bind";
const cx = className.bind(styles);

// Assets
import IconRoundRectSm from '@/assets/svg/round-rect-sm.svg';
import IconClose from '@/assets/svg/close.svg';

// Constants
const HOVER_MODE_ENABLED = true;
const HOVER_ZONE_PERCENT = 0.10; // Hover zones as percentage of tray width
const HOVER_SCROLL_SPEED = 6; // Base scroll speed for hover (pixels per frame)

// ----------------------------------------

interface VideoTrayProps {
  videos: RockHallTvVideo[];
  activeVideoSlug?: string;
  isOpen: boolean;
  isAnimationComplete: boolean;
  onToggle: () => void;
  onVideoSelect: (video: RockHallTvVideo) => void;
  onAnimationComplete: () => void;
  trayRowWrapperRef: React.RefObject<HTMLDivElement>;
  isTouchDevice?: boolean;
  trayButtonLabels: {
    videoTrayOpen: string;
    videoTrayClose: string;
  }
  rrtvAriaLabels: RockHallTvContent['rrtvAriaLabels'];
}

export default function VideoTray({
  videos,
  activeVideoSlug,
  isOpen,
  isAnimationComplete,
  onToggle,
  onVideoSelect,
  onAnimationComplete,
  trayRowWrapperRef,
  isTouchDevice = false,
  trayButtonLabels,
  rrtvAriaLabels,
}: VideoTrayProps) {

  const trayVideoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [videoReadyStates, setVideoReadyStates] = useState<boolean[]>([]);
  const [videoFadedInStates, setVideoFadedInStates] = useState<boolean[]>([]);
  const closeTrayButtonRef = useRef<HTMLButtonElement>(null);
  const trayItemButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // -------------------- Draggable

  const trayRowRef = useRef<HTMLDivElement>(null);
  const draggableRef = useRef<Draggable[] | null>(null);
  const hoverScrollAnimRef = useRef<number | null>(null);
  const hoverDirectionRef = useRef<'left' | 'right' | null>(null);
  const hoverSpeedRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Store event handlers in refs for cleanup
  const handleMouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const handleMouseLeaveRef = useRef<(() => void) | null>(null);

  // Helper function to scroll a tray item into view (called on focus)
  const scrollItemIntoView = useCallback((index: number) => {
    const button = trayItemButtonRefs.current[index];
    const trayRow = trayRowRef.current;
    const trayRowWrapper = trayRowWrapperRef.current;

    if (!button || !trayRow || !trayRowWrapper) return;

    const buttonRect = button.getBoundingClientRect();
    const wrapperRect = trayRowWrapper.getBoundingClientRect();

    // Check if button is out of view
    const isOutOfViewLeft = buttonRect.left < wrapperRect.left;
    const isOutOfViewRight = buttonRect.right > wrapperRect.right;

    if (!isOutOfViewLeft && !isOutOfViewRight) return; // Already in view

    // Calculate target position
    const currentX = gsap.getProperty(trayRow, 'x') as number;
    const offset = isOutOfViewLeft
      ? buttonRect.left - wrapperRect.left
      : buttonRect.right - wrapperRect.right;
    const targetX = currentX - offset;

    // Clamp to bounds
    const maxScroll = Math.max(0, trayRow.scrollWidth - trayRowWrapper.clientWidth);
    const clampedX = Math.max(-maxScroll, Math.min(0, targetX));

    // Animate scroll
    gsap.to(trayRow, {
      x: clampedX,
      duration: 0.5,
      ease: 'power2.out',
      onUpdate: () => {
        draggableRef.current?.[0]?.update();
      }
    });
  }, []); // Empty deps - all refs are stable

  // -------------------- Draggable & Hover Scroll Setup
  // Initialize Draggable and hover scroll functionality when tray is open

  useEffect(() => {
    if (!isOpen || !isAnimationComplete || !trayRowRef.current || !trayRowWrapperRef.current) {
      return;
    }

    // -------------------- TOUCH DEVICE: Use native scrolling

    // On touch devices with native smooth scrolling (iOS, Android),
    // we skip all Draggable and hover scroll logic and rely on native overflow scrolling.
    if (isTouchDevice) {
      // No custom scroll logic needed - native scrolling handles everything
      return;
    }

    // -------------------- NON-TOUCH DEVICE: Use Draggable + hover scroll

    // Register Draggable plugin
    gsap.registerPlugin(Draggable);

    // Elements
    const trayRow = trayRowRef.current;
    const trayRowWrapper = trayRowWrapperRef.current;

    // Calculate bounds function (reusable for resize)
    const calculateBounds = () => {
      const rowWidth = trayRow.scrollWidth;
      const wrapperWidth = trayRowWrapper.offsetWidth;
      const maxSlide = rowWidth - wrapperWidth;
      return { rowWidth, wrapperWidth, maxSlide };
    };

    // Initial calculation
    let { maxSlide } = calculateBounds();

    // Only create Draggable if there's content to drag
    if (maxSlide > 0) {

      // Create Draggable instance
      draggableRef.current = Draggable.create(trayRow, {
        type: 'x',
        trigger: trayRowWrapper, // Allow dragging from anywhere in the wrapper (including gaps)
        bounds: {
          minX: -maxSlide,
          maxX: 0,
        },
        inertia: true,
        overshootTolerance: 0, // Prevent bounce at boundaries
        onPress: () => {
          // User pressed down (before drag starts)
          isDraggingRef.current = true;
          setIsDragging(true);

          // Stop hover scroll animation (only if hover mode enabled)
          if (HOVER_MODE_ENABLED && hoverScrollAnimRef.current !== null) {
            cancelAnimationFrame(hoverScrollAnimRef.current);
            hoverScrollAnimRef.current = null;
          }
          if (HOVER_MODE_ENABLED) {
            hoverDirectionRef.current = null;
            hoverSpeedRef.current = 0;
          }
        },
        onRelease: () => {
          // User released (drag ended, throw may continue)
          isDraggingRef.current = false;
          setIsDragging(false);
        },
      });

      // -------------------- Hover scroll functionality

      if (HOVER_MODE_ENABLED) {
        const wrapperRect = trayRowWrapper.getBoundingClientRect();
        const hoverZoneWidth = wrapperRect.width * HOVER_ZONE_PERCENT;

        // Animation loop for hover scrolling
        const animateHoverScroll = () => {
          const direction = hoverDirectionRef.current;
          const speed = hoverSpeedRef.current;

          if (direction && speed > 0) {
            const currentX = gsap.getProperty(trayRow, 'x') as number;

            if (direction === 'right' && currentX < 0) {
              // Scroll right (toward 0)
              const newX = Math.min(0, currentX + speed);
              gsap.set(trayRow, { x: newX });

              if (newX < 0) {
                hoverScrollAnimRef.current = requestAnimationFrame(animateHoverScroll);
              } else {
                hoverDirectionRef.current = null;
              }
            } else if (direction === 'left' && currentX > -maxSlide) {
              // Scroll left (toward -maxSlide)
              const newX = Math.max(-maxSlide, currentX - speed);
              gsap.set(trayRow, { x: newX });

              if (newX > -maxSlide) {
                hoverScrollAnimRef.current = requestAnimationFrame(animateHoverScroll);
              } else {
                hoverDirectionRef.current = null;
              }
            }
          }
        };

        // Mouse move handler to detect hover zones
        const handleMouseMove = (e: MouseEvent) => {
          // Don't process hover scroll while actively dragging
          if (isDraggingRef.current) return;

          const rect = trayRowWrapper.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const currentX = gsap.getProperty(trayRow, 'x') as number;

          // Check if in left hover zone and can scroll right
          if (mouseX < hoverZoneWidth && currentX < 0) {
            // Calculate speed based on proximity to edge (closer = faster)
            const proximity = 1 - (mouseX / hoverZoneWidth);
            const speed = HOVER_SCROLL_SPEED * proximity;

            hoverDirectionRef.current = 'right';
            hoverSpeedRef.current = speed;

            // Start animation loop if not already running
            if (hoverScrollAnimRef.current === null) {
              hoverScrollAnimRef.current = requestAnimationFrame(animateHoverScroll);
            }
          }
          // Check if in right hover zone and can scroll left
          else if (mouseX > rect.width - hoverZoneWidth && currentX > -maxSlide) {
            // Calculate speed based on proximity to edge (closer = faster)
            const proximity = (mouseX - (rect.width - hoverZoneWidth)) / hoverZoneWidth;
            const speed = HOVER_SCROLL_SPEED * proximity;

            hoverDirectionRef.current = 'left';
            hoverSpeedRef.current = speed;

            // Start animation loop if not already running
            if (hoverScrollAnimRef.current === null) {
              hoverScrollAnimRef.current = requestAnimationFrame(animateHoverScroll);
            }
          }
          // Outside hover zones, stop scrolling
          else {
            hoverDirectionRef.current = null;
            hoverSpeedRef.current = 0;

            if (hoverScrollAnimRef.current !== null) {
              cancelAnimationFrame(hoverScrollAnimRef.current);
              hoverScrollAnimRef.current = null;
            }
          }
        };

        // Mouse leave handler
        const handleMouseLeave = () => {
          hoverDirectionRef.current = null;
          hoverSpeedRef.current = 0;

          if (hoverScrollAnimRef.current !== null) {
            cancelAnimationFrame(hoverScrollAnimRef.current);
            hoverScrollAnimRef.current = null;
          }
        };

        // Store handlers in refs for cleanup
        handleMouseMoveRef.current = handleMouseMove;
        handleMouseLeaveRef.current = handleMouseLeave;

        // Add event listeners
        trayRowWrapper.addEventListener('mousemove', handleMouseMove);
        trayRowWrapper.addEventListener('mouseleave', handleMouseLeave);
      }

      // -------------------- Resize handler

      const handleResize = () => {
        const { maxSlide: newMaxSlide } = calculateBounds();

        // Update maxSlide for hover scroll calculations
        maxSlide = newMaxSlide;

        if (newMaxSlide <= 0) {
          // No room to scroll - reset position to 0
          gsap.set(trayRow, { x: 0 });

          // Kill draggable if it exists
          if (draggableRef.current) {
            draggableRef.current.forEach((d) => d.kill());
            draggableRef.current = null;
          }
        } else {
          // There's room to scroll
          const currentX = gsap.getProperty(trayRow, 'x') as number;

          // Check if current position is out of new bounds
          if (currentX < -newMaxSlide) {
            // Position is too far left, snap to new max
            gsap.set(trayRow, { x: -newMaxSlide });
          }

          // Update or create draggable with new bounds
          if (draggableRef.current && draggableRef.current[0]) {
            draggableRef.current[0].applyBounds({
              minX: -newMaxSlide,
              maxX: 0,
            });
          } else {
            // Recreate draggable if it was killed
            draggableRef.current = Draggable.create(trayRow, {
              type: 'x',
              trigger: trayRowWrapper,
              bounds: {
                minX: -newMaxSlide,
                maxX: 0,
              },
              inertia: true,
              overshootTolerance: 0,
              onPress: () => {
                isDraggingRef.current = true;
                setIsDragging(true);
                if (HOVER_MODE_ENABLED && hoverScrollAnimRef.current !== null) {
                  cancelAnimationFrame(hoverScrollAnimRef.current);
                  hoverScrollAnimRef.current = null;
                }
                if (HOVER_MODE_ENABLED) {
                  hoverDirectionRef.current = null;
                  hoverSpeedRef.current = 0;
                }
              },
              onRelease: () => {
                isDraggingRef.current = false;
                setIsDragging(false);
              },
            });
          }
        }
      };

      // Add resize listener
      window.addEventListener('resize', handleResize);

      // Cleanup function
      return () => {
        if (draggableRef.current) {
          draggableRef.current.forEach((d) => d.kill());
          draggableRef.current = null;
        }
        if (HOVER_MODE_ENABLED) {
          if (hoverScrollAnimRef.current !== null) {
            cancelAnimationFrame(hoverScrollAnimRef.current);
            hoverScrollAnimRef.current = null;
          }
          if (handleMouseMoveRef.current) {
            trayRowWrapper.removeEventListener('mousemove', handleMouseMoveRef.current);
          }
          if (handleMouseLeaveRef.current) {
            trayRowWrapper.removeEventListener('mouseleave', handleMouseLeaveRef.current);
          }
        }
        window.removeEventListener('resize', handleResize);
      };

    }
  }, [isOpen, isAnimationComplete, videos.length, isTouchDevice]);

  // -------------------- Focus Trap
  // When tray is open, trap focus within the tray (close button + video items)

  useEffect(() => {
    if (!isOpen || !isAnimationComplete) return;

    // Focus the close button when tray opens
    if (closeTrayButtonRef.current) {
      closeTrayButtonRef.current.focus();
    }

    // Handle keyboard navigation to trap focus
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trap Tab key
      if (e.key !== 'Tab') return;

      // Get all focusable elements in the tray
      const focusableElements: HTMLButtonElement[] = [
        closeTrayButtonRef.current,
        ...trayItemButtonRefs.current.filter(Boolean)
      ].filter((el): el is HTMLButtonElement => el !== null);

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // If Shift+Tab on first element, go to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // If Tab on last element, go to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isAnimationComplete, videos.length]);

  // -------------------- Memoize tray items to prevent re-rendering

  const trayItems = useMemo(() => {
    return videos.map((video, index) => {
      const isActive = video.slug === activeVideoSlug;
      return {
        video,
        index,
        isActive,
        thumbnail: video.rhtvVideoContent?.thumbnail ? (
          <Image image={video.rhtvVideoContent.thumbnail} />
        ) : video.rhtvVideoContent?.youtubeVideoId ? (
          <YoutubeThumbnail
            key={video.rhtvVideoContent.youtubeVideoId}
            youtubeId={video.rhtvVideoContent.youtubeVideoId}
          />
        ) : null
      };
    });
  }, [videos, activeVideoSlug]);

  // -------------------- Render

  return (
    <div className={cx(["tray",
        { open: isOpen, "touch-device": isTouchDevice }
      ])}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={cx("tray-content")}>

        {/* Tray open/close buttons */}
        <div className={cx("tray-ctas")}>

          {/* Open CTA (shown when closed) */}
          <motion.button className={cx("tray-cta", "open-playlist")}
            onClick={onToggle}
            animate={{
              opacity: isOpen ? 0 : 1,
              y: isOpen ? -10 : 0,
            }}
            transition={{
              duration: ANIMATION_CONFIG.DUR_TRAY_CTA,
              ease: 'easeOut',
              delay: isOpen ? 0 : ANIMATION_CONFIG.DUR_TRAY_OPEN
            }}
            style={{ pointerEvents: isOpen ? 'none' : 'auto' }}
            tabIndex={isOpen ? -1 : 0}
            aria-hidden={isOpen}
          >
            <div className={cx("icon")}>
              <IconRoundRectSm /> <IconRoundRectSm /> <IconRoundRectSm />
            </div>
            <span className={cx("tag-s")}>{trayButtonLabels.videoTrayOpen}</span>
          </motion.button>

          {/* Close CTA (shown when open) */}
          <motion.button
            ref={closeTrayButtonRef}
            className={cx("tray-cta", "close-playlist")}
            onClick={onToggle}
            animate={{
              opacity: isOpen ? 1 : 0,
              y: isOpen ? 0 : 10,
              x: '-50%',
            }}
            transition={{
              duration: ANIMATION_CONFIG.DUR_TRAY_CTA,
              ease: 'easeOut',
              delay: isOpen ? ANIMATION_CONFIG.DUR_TRAY_OPEN : 0
            }}
            style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
            tabIndex={isOpen ? 0 : -1}
            aria-hidden={!isOpen}
          >
            <div className={cx("icon")}><IconClose /></div>
            <span className={cx("tag-s")}>{trayButtonLabels.videoTrayClose}</span>
          </motion.button>

        </div>

        {/* Tray row - slides up/down */}
        <motion.div
          ref={trayRowWrapperRef}
          className={cx(["tray-row-wrapper", { dragging: isDragging }, { "touch-device": isTouchDevice }])}
          initial={{ y: '175%' }}
          animate={{ y: isOpen ? 0 : '175%' }}
          transition={{ duration: ANIMATION_CONFIG.DUR_TRAY_OPEN, ease: 'easeInOut' }}
          onAnimationComplete={onAnimationComplete}
          style={{ pointerEvents: (isOpen && isAnimationComplete) ? 'auto' : 'none' }}
          role="region"
          aria-label={rrtvAriaLabels.videoPlaylist}
          aria-hidden={!isOpen}
        >
          <div
            className={cx("tray-row", { "touch-device": isTouchDevice })}
            ref={trayRowRef}
            role="list"
            aria-label={rrtvAriaLabels.channelVideos}
          >
            {/* Loop through tray items */}
            {trayItems.map(({ video, index, isActive, thumbnail }) => {

              // Video states
              const isVideoReady = videoReadyStates[index];
              const isVideoFadedIn = videoFadedInStates[index];

              // Handlers
              const handleMouseEnter = () => {
                if (isTouchDevice) return; // Skip on touch devices
                const videoEl = trayVideoRefs.current[index];
                if (videoEl) {
                  videoEl.play().catch(() => {
                    // Ignore play errors
                  });
                }
              };
              const handleMouseLeave = () => {
                if (isTouchDevice) return; // Skip on touch devices
                const videoEl = trayVideoRefs.current[index];
                if (videoEl) {
                  videoEl.pause();
                }
              };
              const handleCanPlay = () => {
                setVideoReadyStates((prev) => {
                  const newStates = [...prev];
                  newStates[index] = true;
                  return newStates;
                });
              };
              const handleVideoAnimationComplete = (definition: any) => {
                if (definition.opacity === 1) {
                  setVideoFadedInStates((prev) => {
                    const newStates = [...prev];
                    newStates[index] = true;
                    return newStates;
                  });
                }
              };
              const handleSelect = (e: React.MouseEvent | React.TouchEvent) => {
                // Click handler - on touch devices, don't prevent default to allow scrolling
                onVideoSelect(video);
              };

              // Get video title for accessibility
              const videoTitle = video.title || 'Untitled video';
              const ariaLabel = isActive
                ? replaceStringVar(rrtvAriaLabels.videoPlaying, { title: videoTitle })
                : replaceStringVar(rrtvAriaLabels.playVideo, { title: videoTitle });

              // Return the tray item render
              return (
                <button
                  ref={(el) => { trayItemButtonRefs.current[index] = el; }}
                  className={cx("tray-item", { active: isActive })}
                  key={video.slug || index}
                  onClick={handleSelect}
                  onFocus={() => scrollItemIntoView(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onVideoSelect(video);
                    }
                  }}
                  aria-label={ariaLabel}
                  aria-current={isActive ? 'true' : undefined}
                  aria-pressed={isActive}
                  tabIndex={(isOpen && isAnimationComplete) ? 0 : -1}
                  {...(!isTouchDevice && {
                    onMouseEnter: handleMouseEnter,
                    onMouseLeave: handleMouseLeave,
                  })}
                >
                  {/* Thumbnail */}
                  <div className={cx("tray-item-media")}>
                    {/* Thumbnail image */}
                    {!isVideoFadedIn && thumbnail}

                    {/* Video preview (optional) */}
                    {video.rhtvVideoContent?.previewVideo && (
                      <motion.video
                        ref={(el) => { trayVideoRefs.current[index] = el; }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isVideoReady ? 1 : 0 }}
                        transition={{ duration: 0.35, ease: easeOutQuad }}
                        onAnimationComplete={handleVideoAnimationComplete}
                        src={video.rhtvVideoContent.previewVideo}
                        muted
                        loop
                        playsInline
                        onCanPlay={handleCanPlay}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>

      </div>
    </div>
  );

}
